import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axiosConfig from '../../../../api/axiosConfig';
import { 
  FiUsers, FiDollarSign, FiCalendar, FiTrendingUp, FiDownload, 
  FiPrinter, FiEye, FiFilter, FiSearch, FiX, FiChevronDown,
  FiChevronUp, FiPieChart, FiBarChart2, FiFileText, FiMail,
  FiClock, FiCheckCircle, FiXCircle, FiBriefcase, FiActivity
} from 'react-icons/fi';
import * as XLSX from 'xlsx';

const DanismanReportPage = () => {
  const [danismanlar, setDanismanlar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('yearly');
  const [selectedDanisman, setSelectedDanisman] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [reportType, setReportType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'toplamAlacak', direction: 'desc' });
  const [showGraphs, setShowGraphs] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [selectedDanismanlar, setSelectedDanismanlar] = useState(new Set());
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [customDateMode, setCustomDateMode] = useState(false);
  
  // Cache için state'ler
  const [derslerCache, setDerslerCache] = useState([]);
  const [kursSatislarCache, setKursSatislarCache] = useState([]);

  // Dönem bazlı ödeme hesaplama - DANISMAN MANTIĞI
  const calculatePeriodPayment = (danismanId, periodDersler, kursSatislar) => {
    let toplam40 = 0, toplam60 = 0, toplam80 = 0, toplam120 = 0;
    let toplamUcret = 0;
    const dersDetaylari = [];

    periodDersler.forEach(ders => {
      // Önce bu dersin kurs bilgisini bul
      const kurs = kursSatislar.find(k => String(k.id) === String(ders.kurs_id));
      if (!kurs) return;

      // Danışman bu kursta danışman mı kontrol et
      let danismanPayi = 0;
      
      // Kurs satışta danışman ID'lerini kontrol et
      if (String(kurs.danisman1) === String(danismanId)) {
        danismanPayi = parseFloat(ders.danisman1) || 0;
      } else if (String(kurs.danisman2) === String(danismanId)) {
        danismanPayi = parseFloat(ders.danisman2) || 0;
      } else if (String(kurs.danisman3) === String(danismanId)) {
        danismanPayi = parseFloat(ders.danisman3) || 0;
      }

      // Debug için
      if (danismanPayi > 0) {
        console.log(`Danışman ${danismanId} için ders ${ders.id} bulundu:`, {
          kurs_id: ders.kurs_id,
          kurs_danisman1: kurs.danisman1,
          kurs_danisman2: kurs.danisman2,
          kurs_danisman3: kurs.danisman3,
          ders_danisman1_payi: ders.danisman1,
          ders_danisman2_payi: ders.danisman2,
          ders_danisman3_payi: ders.danisman3,
          hesaplananPay: danismanPayi
        });
      }
      
      if (danismanPayi === 0) return;

      const dersSure = parseInt(ders.ders_sure);
      if (dersSure == 40) toplam40 += danismanPayi;
      if (dersSure == 60) toplam60 += danismanPayi;
      if (dersSure == 80) toplam80 += danismanPayi;
      if (dersSure == 120) toplam120 += danismanPayi;

      toplamUcret += danismanPayi;

      dersDetaylari.push({
        id: ders.id,
        tarih: ders.baslangic_tarihi,
        konu: ders.ders_konusu,
        sure: ders.ders_sure,
        ucret: danismanPayi,
        ogrenciId: ders.ogrenci_id,
        ogretmenId: ders.ogretmen_id,
        ogrenciAdi: ders.ogrenci_name || ders.ogrenci_adi || '-',
        ogretmenAdi: ders.ogretmen_name || ders.ogretmen_adi || '-',
        ogrenciTelefon: ders.parent_phone || '-',
        ogretmenTelefon: ders.ogretmen_telefon || '-'
      });
    });

    return { toplamUcret, toplam40, toplam60, toplam80, toplam120, dersDetaylari };
  };

  // Dersleri tarihe göre gruplama
  const groupDerslerByDate = (dersler) => {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1;
    const currentYear = currentDate.getFullYear();
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const weekStart = new Date(currentDate);
    weekStart.setDate(currentDate.getDate() - currentDate.getDay());
    weekStart.setHours(0, 0, 0, 0);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 7);

    return {
      monthly: dersler.filter(ders => {
        const dersDate = new Date(ders.baslangic_tarihi);
        return dersDate.getMonth() + 1 === currentMonth && 
               dersDate.getFullYear() === currentYear;
      }),
      daily: dersler.filter(ders => {
        const dersDate = new Date(ders.baslangic_tarihi);
        return dersDate >= today && dersDate < tomorrow;
      }),
      weekly: dersler.filter(ders => {
        const dersDate = new Date(ders.baslangic_tarihi);
        return dersDate >= weekStart && dersDate < weekEnd;
      }),
      yearly: dersler
    };
  };

  // Helper fonksiyonları
  const getSelectedPeriodAmount = useCallback((danisman) => {
    if (!danisman) return 0;
    if (customDateMode) return danisman.customAlacak || 0;
    switch (selectedPeriod) {
      case 'daily': return danisman.gunlukAlacak || 0;
      case 'weekly': return danisman.haftalikAlacak || 0;
      case 'current': return danisman.aylikAlacak || 0;
      case 'yearly': return danisman.yillikAlacak || 0;
      default: return 0;
    }
  }, [selectedPeriod, customDateMode]);

  const getSelectedPeriodDetails = useCallback((danisman) => {
    if (!danisman) return [];
    if (customDateMode) return danisman.customDersDetaylari || [];
    switch (selectedPeriod) {
      case 'daily': return danisman.gunlukDersDetaylari || [];
      case 'weekly': return danisman.haftalikDersDetaylari || [];
      case 'current': return danisman.dersDetaylari || [];
      case 'yearly': return danisman.yillikDersDetaylari || [];
      default: return [];
    }
  }, [selectedPeriod, customDateMode]);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (dateRange.start && dateRange.end && danismanlar.length > 0) {
      updateCustomDateData();
    }
  }, [dateRange]);

  // Paralel veri çekme
  const fetchInitialData = async () => {
    setLoading(true);
    try {
      // Tüm API çağrılarını paralel yap
      const [danismanRes, derslerRes, kursSatisRes] = await Promise.all([
        axiosConfig.get('/admin/danismans'), // Danışman API endpoint'i
        axiosConfig.get('/admin/ogretmenders'),
        axiosConfig.get('/admin/kurssatis') // KursSatis verilerini de çekmemiz gerekiyor
      ]);

      const danismanlarData = danismanRes.data || [];
      const dersler = Array.isArray(derslerRes.data) ? derslerRes.data : (derslerRes.data.data || []);
      const kursSatislar = Array.isArray(kursSatisRes.data) ? kursSatisRes.data : (kursSatisRes.data.data || []);

      // Cache'e kaydet
      setDerslerCache(dersler);
      setKursSatislarCache(kursSatislar);

      console.log('Toplam danışman sayısı:', danismanlarData.length);
      console.log('Toplam ders sayısı:', dersler.length);
      console.log('Toplam kurs satış sayısı:', kursSatislar.length);
      
      // Debug için örnek veriler
      console.log('Örnek ders:', dersler[0]);
      console.log('Örnek danışman:', danismanlarData[0]);
      console.log('Örnek kurs satış:', kursSatislar[0]);

      // Danışman verilerini işle
      const processedDanismanlar = processDanismanlarData(danismanlarData, dersler, kursSatislar);
      setDanismanlar(processedDanismanlar);
    } catch (error) {
      console.error('Veri yükleme hatası:', error);
      alert('Veri yüklenirken hata oluştu!');
    } finally {
      setLoading(false);
    }
  };

  // Özel tarih aralığı için veri güncelleme
  const updateCustomDateData = useCallback(() => {
    const startDate = new Date(dateRange.start);
    const endDate = new Date(dateRange.end);
    endDate.setHours(23, 59, 59, 999);

    const customDersler = derslerCache.filter(ders => {
      const dersDate = new Date(ders.baslangic_tarihi);
      return dersDate >= startDate && dersDate <= endDate;
    });

    const updatedDanismanlar = danismanlar.map(danisman => {
      const customData = calculatePeriodPayment(danisman.id, customDersler, kursSatislarCache);
      
      return {
        ...danisman,
        customAlacak: customData.toplamUcret,
        customDersDetaylari: customData.dersDetaylari
      };
    });

    setDanismanlar(updatedDanismanlar);
  }, [dateRange, derslerCache, kursSatislarCache, danismanlar]);

  // Danışman verilerini işleme
  const processDanismanlarData = useCallback((danismanlarData, dersler, kursSatislar) => {
    // Dersleri tarihe göre grupla
    const derslerByDate = groupDerslerByDate(dersler);

    return danismanlarData.map(danisman => {
      console.log(`Danışman ${danisman.adsoyad} (ID: ${danisman.id}) için hesaplama yapılıyor...`);

      // Her dönem için hesaplama yap
      const aylikData = calculatePeriodPayment(danisman.id, derslerByDate.monthly, kursSatislar);
      const gunlukData = calculatePeriodPayment(danisman.id, derslerByDate.daily, kursSatislar);
      const haftalikData = calculatePeriodPayment(danisman.id, derslerByDate.weekly, kursSatislar);
      const yillikData = calculatePeriodPayment(danisman.id, derslerByDate.yearly, kursSatislar);

      console.log(`${danisman.adsoyad} - Aylık: ${aylikData.toplamUcret}, Yıllık: ${yillikData.toplamUcret}`);

      return {
        ...danisman,
        aylikAlacak: aylikData.toplamUcret,
        gunlukAlacak: gunlukData.toplamUcret,
        haftalikAlacak: haftalikData.toplamUcret,
        yillikAlacak: yillikData.toplamUcret,
        toplam40: aylikData.toplam40,
        toplam60: aylikData.toplam60,
        toplam80: aylikData.toplam80,
        toplam120: aylikData.toplam120,
        dersDetaylari: aylikData.dersDetaylari,
        gunlukDersDetaylari: gunlukData.dersDetaylari,
        haftalikDersDetaylari: haftalikData.dersDetaylari,
        yillikDersDetaylari: yillikData.dersDetaylari,
        toplamDersSayisi: yillikData.dersDetaylari.length
      };
    });
  }, []);

  // Sıralama fonksiyonu
  const handleSort = useCallback((key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  // Memoized summary stats
  const summaryStats = useMemo(() => {
    let filteredDanismanlar = [...danismanlar];

    // Arama filtresi
    if (searchTerm) {
      filteredDanismanlar = filteredDanismanlar.filter(d => 
        d.adsoyad.toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(d.id).includes(searchTerm)
      );
    }

    const selectedPeriodStats = {
      toplam40: 0,
      toplam60: 0,
      toplam80: 0,
      toplam120: 0
    };

    filteredDanismanlar.forEach(danisman => {
      if (selectedPeriod === 'current' && !customDateMode) {
        selectedPeriodStats.toplam40 += danisman.toplam40 || 0;
        selectedPeriodStats.toplam60 += danisman.toplam60 || 0;
        selectedPeriodStats.toplam80 += danisman.toplam80 || 0;
        selectedPeriodStats.toplam120 += danisman.toplam120 || 0;
      } else {
        const periodDetails = getSelectedPeriodDetails(danisman);
        periodDetails.forEach(ders => {
          const dersSure = parseInt(ders.sure);
          const ucret = ders.ucret;
          switch(dersSure) {
            case 40: selectedPeriodStats.toplam40 += ucret; break;
            case 60: selectedPeriodStats.toplam60 += ucret; break;
            case 80: selectedPeriodStats.toplam80 += ucret; break;
            case 120: selectedPeriodStats.toplam120 += ucret; break;
          }
        });
      }
    });

    if (reportType === 'with-payment') {
      filteredDanismanlar = filteredDanismanlar.filter(d => getSelectedPeriodAmount(d) > 0);
    } else if (reportType === 'no-payment') {
      filteredDanismanlar = filteredDanismanlar.filter(d => getSelectedPeriodAmount(d) === 0);
    }

    const summary = filteredDanismanlar.reduce((acc, danisman) => {
      const amount = getSelectedPeriodAmount(danisman);
      
      acc.toplamDanisman++;
      if (amount > 0) acc.alacakliDanisman++;
      else acc.alacaksizDanisman++;
      
      acc.toplamAlacak += amount;
      
      return acc;
    }, {
      toplamDanisman: 0,
      alacakliDanisman: 0,
      alacaksizDanisman: 0,
      toplamAlacak: 0,
      toplam40: selectedPeriodStats.toplam40,
      toplam60: selectedPeriodStats.toplam60,
      toplam80: selectedPeriodStats.toplam80,
      toplam120: selectedPeriodStats.toplam120
    });

    return summary;
  }, [danismanlar, selectedPeriod, reportType, searchTerm, customDateMode, getSelectedPeriodAmount, getSelectedPeriodDetails]);

  // Memoized filtered danismanlar
  const filteredDanismanlar = useMemo(() => {
    let filtered = [...danismanlar];

    // Arama filtresi
    if (searchTerm) {
      filtered = filtered.filter(d => 
        d.adsoyad.toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(d.id).includes(searchTerm)
      );
    }

    if (reportType === 'with-payment') {
      filtered = filtered.filter(d => getSelectedPeriodAmount(d) > 0);
    } else if (reportType === 'no-payment') {
      filtered = filtered.filter(d => getSelectedPeriodAmount(d) === 0);
    }

    // Sıralama
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch(sortConfig.key) {
        case 'adsoyad':
          aValue = a.adsoyad;
          bValue = b.adsoyad;
          break;
        case 'toplamAlacak':
          aValue = getSelectedPeriodAmount(a);
          bValue = getSelectedPeriodAmount(b);
          break;
        case 'dersSayisi':
          aValue = getSelectedPeriodDetails(a).length;
          bValue = getSelectedPeriodDetails(b).length;
          break;
        default:
          aValue = a[sortConfig.key];
          bValue = b[sortConfig.key];
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [danismanlar, reportType, searchTerm, sortConfig, getSelectedPeriodAmount, getSelectedPeriodDetails]);

  const formatCurrency = useCallback((amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 2
    }).format(amount || 0);
  }, []);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR');
  }, []);

  const handleDanismanClick = useCallback((danisman) => {
    setSelectedDanisman(danisman);
    setShowDetailsModal(true);
  }, []);

  const toggleRowExpansion = useCallback((danismanId) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(danismanId)) {
        newSet.delete(danismanId);
      } else {
        newSet.add(danismanId);
      }
      return newSet;
    });
  }, []);

  const toggleDanismanSelection = useCallback((danismanId) => {
    setSelectedDanismanlar(prev => {
      const newSet = new Set(prev);
      if (newSet.has(danismanId)) {
        newSet.delete(danismanId);
      } else {
        newSet.add(danismanId);
      }
      return newSet;
    });
  }, []);

  const selectAllDanismanlar = useCallback(() => {
    if (selectedDanismanlar.size === filteredDanismanlar.length) {
      setSelectedDanismanlar(new Set());
    } else {
      setSelectedDanismanlar(new Set(filteredDanismanlar.map(d => d.id)));
    }
  }, [filteredDanismanlar, selectedDanismanlar]);

  const exportToExcel = useCallback(() => {
    const dataToExport = filteredDanismanlar.map(danisman => {
      const periodAmount = getSelectedPeriodAmount(danisman);
      const periodDetails = getSelectedPeriodDetails(danisman);
      
      return {
        'ID': danisman.id,
        'Danışman Adı': danisman.adsoyad,
        'Ders Sayısı': periodDetails.length,
        'Alacak Tutarı': periodAmount,
        'Durum': periodAmount > 0 ? 'Alacaklı' : 'Alacak Yok',
        '40 Dk Toplam': danisman.toplam40 || 0,
        '60 Dk Toplam': danisman.toplam60 || 0,
        '80 Dk Toplam': danisman.toplam80 || 0,
        '120 Dk Toplam': danisman.toplam120 || 0
      };
    });

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Danışman Raporu");
    
    const fileName = `danisman_raporu_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  }, [filteredDanismanlar, getSelectedPeriodAmount, getSelectedPeriodDetails]);

  const exportSelectedToExcel = useCallback(() => {
    const selectedData = filteredDanismanlar.filter(d => selectedDanismanlar.has(d.id));
    
    const detailedData = [];
    selectedData.forEach(danisman => {
      const periodDetails = getSelectedPeriodDetails(danisman);
      periodDetails.forEach(ders => {
        detailedData.push({
          'Danışman ID': danisman.id,
          'Danışman Adı': danisman.adsoyad,
          'Ders ID': ders.id,
          'Ders Tarihi': formatDate(ders.tarih),
          'Ders Konusu': ders.konu || '-',
          'Ders Süresi': `${ders.sure} dk`,
          'Danışman Payı': ders.ucret
        });
      });
    });

    const ws = XLSX.utils.json_to_sheet(detailedData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Detaylı Ders Raporu");
    
    const fileName = `danisman_detayli_rapor_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  }, [filteredDanismanlar, selectedDanismanlar, getSelectedPeriodDetails, formatDate]);

  const sendBulkEmail = useCallback(() => {
    const selectedData = filteredDanismanlar.filter(d => selectedDanismanlar.has(d.id));
    alert(`${selectedData.length} danışmana toplu email gönderilecek. (Bu özellik henüz aktif değil)`);
  }, [filteredDanismanlar, selectedDanismanlar]);

  const printReport = useCallback(() => {
    window.print();
  }, []);

  const getPeriodText = useCallback(() => {
    if (customDateMode && dateRange.start && dateRange.end) {
      return `${formatDate(dateRange.start)} - ${formatDate(dateRange.end)}`;
    }
    const periodTexts = {
      'current': 'Bu Ay',
      'daily': 'Bugün',
      'weekly': 'Bu Hafta',
      'yearly': 'Tüm Zamanlar'
    };
    return periodTexts[selectedPeriod] || 'Tümü';
  }, [selectedPeriod, customDateMode, dateRange, formatDate]);

  const getGraphData = useMemo(() => {
    const top10 = filteredDanismanlar
      .slice(0, 10)
      .map(d => ({
        name: d.adsoyad,
        value: getSelectedPeriodAmount(d)
      }));

    const durationData = [
      { name: '40 Dakika', value: summaryStats.toplam40 },
      { name: '60 Dakika', value: summaryStats.toplam60 },
      { name: '80 Dakika', value: summaryStats.toplam80 },
      { name: '120 Dakika', value: summaryStats.toplam120 }
    ];

    return { top10, durationData };
  }, [filteredDanismanlar, summaryStats, getSelectedPeriodAmount]);

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Danışmanlar Raporu</h1>
              <p className="text-gray-600 mt-1">Danışmanların ders ve ödeme durumları</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowGraphs(!showGraphs)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
              >
                {showGraphs ? <FiPieChart className="w-5 h-5" /> : <FiBarChart2 className="w-5 h-5" />}
                {showGraphs ? 'Grafikler' : 'Tablo'}
              </button>
              <button
                onClick={printReport}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2"
              >
                <FiPrinter className="w-5 h-5" />
                Yazdır
              </button>
              <button
                onClick={exportToExcel}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
              >
                <FiDownload className="w-5 h-5" />
                Excel İndir
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Period Filter */}
            <div className="lg:col-span-2">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Dönem Seçimi</h3>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: 'daily', label: 'Günlük', icon: FiClock },
                  { value: 'weekly', label: 'Haftalık', icon: FiCalendar },
                  { value: 'current', label: 'Aylık', icon: FiCalendar },
                  { value: 'yearly', label: 'Tüm Zamanlar', icon: FiTrendingUp }
                ].map(period => (
                  <button
                    key={period.value}
                    onClick={() => {
                      setSelectedPeriod(period.value);
                      setCustomDateMode(false);
                    }}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                      selectedPeriod === period.value && !customDateMode
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <period.icon className="w-4 h-4" />
                    {period.label}
                  </button>
                ))}
                <button
                  onClick={() => setCustomDateMode(!customDateMode)}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    customDateMode
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Özel Tarih
                </button>
              </div>
              
              {customDateMode && (
                <div className="mt-3 flex gap-3 items-center">
                  <input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <span className="text-gray-500">-</span>
                  <input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              )}
            </div>

            {/* Search */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Arama</h3>
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Danışman adı veya ID ile ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <FiX className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>

            {/* Report Type Filter */}
            <div className="lg:col-span-3">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Rapor Tipi</h3>
              <div className="flex gap-2">
                {[
                  { value: 'all', label: 'Tüm Danışmanlar', icon: FiUsers },
                  { value: 'with-payment', label: 'Alacağı Olanlar', icon: FiCheckCircle },
                  { value: 'no-payment', label: 'Alacağı Olmayanlar', icon: FiXCircle }
                ].map(type => (
                  <button
                    key={type.value}
                    onClick={() => setReportType(type.value)}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                      reportType === type.value
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <type.icon className="w-4 h-4" />
                    {type.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Toplam Danışman</p>
                <p className="text-2xl font-bold text-gray-800">{summaryStats.toplamDanisman}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <FiUsers className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Alacaklı Danışman</p>
                <p className="text-2xl font-bold text-green-600">{summaryStats.alacakliDanisman}</p>
                <p className="text-xs text-gray-500 mt-1">
                  %{summaryStats.toplamDanisman > 0 ? ((summaryStats.alacakliDanisman / summaryStats.toplamDanisman) * 100).toFixed(1) : 0}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <FiTrendingUp className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Alacaksız Danışman</p>
                <p className="text-2xl font-bold text-gray-600">{summaryStats.alacaksizDanisman}</p>
                <p className="text-xs text-gray-500 mt-1">
                  %{summaryStats.toplamDanisman > 0 ? ((summaryStats.alacaksizDanisman / summaryStats.toplamDanisman) * 100).toFixed(1) : 0}
                </p>
              </div>
              <div className="p-3 bg-gray-100 rounded-lg">
                <FiXCircle className="w-6 h-6 text-gray-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Toplam Alacak</p>
                <p className="text-xl font-bold text-purple-600">{formatCurrency(summaryStats.toplamAlacak)}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {getPeriodText()}
                </p>
              </div>
              <div className="p-3 bg-purple-100 rounded-lg">
                <FiDollarSign className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Ders Süre Dağılımı */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow-sm p-4 text-white">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm opacity-90">40 Dakika</p>
                <p className="text-2xl font-bold">{formatCurrency(summaryStats.toplam40)}</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold opacity-90">
                  %{summaryStats.toplamAlacak > 0 ? ((summaryStats.toplam40 / summaryStats.toplamAlacak) * 100).toFixed(0) : 0}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg shadow-sm p-4 text-white">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm opacity-90">60 Dakika</p>
                <p className="text-2xl font-bold">{formatCurrency(summaryStats.toplam60)}</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold opacity-90">
                  %{summaryStats.toplamAlacak > 0 ? ((summaryStats.toplam60 / summaryStats.toplamAlacak) * 100).toFixed(0) : 0}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-r from-pink-500 to-pink-600 rounded-lg shadow-sm p-4 text-white">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm opacity-90">80 Dakika</p>
                <p className="text-2xl font-bold">{formatCurrency(summaryStats.toplam80)}</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold opacity-90">
                  %{summaryStats.toplamAlacak > 0 ? ((summaryStats.toplam80 / summaryStats.toplamAlacak) * 100).toFixed(0) : 0}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg shadow-sm p-4 text-white">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm opacity-90">120 Dakika</p>
                <p className="text-2xl font-bold">{formatCurrency(summaryStats.toplam120)}</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold opacity-90">
                  %{summaryStats.toplamAlacak > 0 ? ((summaryStats.toplam120 / summaryStats.toplamAlacak) * 100).toFixed(0) : 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Graphs Section */}
        {showGraphs && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">En Çok Kazanan 10 Danışman</h3>
              <div className="space-y-3">
                {getGraphData.top10.map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-600 w-8">{index + 1}.</span>
                    <div className="flex-1">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium text-gray-700">{item.name}</span>
                        <span className="text-sm font-bold text-gray-900">{formatCurrency(item.value)}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-indigo-600 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${(item.value / getGraphData.top10[0]?.value || 1) * 100}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Ders Süresine Göre Dağılım</h3>
              <div className="space-y-3">
                {getGraphData.durationData.map((item, index) => {
                  const colors = ['bg-blue-500', 'bg-purple-500', 'bg-pink-500', 'bg-orange-500'];
                  const maxValue = Math.max(...getGraphData.durationData.map(d => d.value));
                  
                  return (
                    <div key={index} className="flex items-center gap-3">
                      <span className="text-sm font-medium text-gray-600 w-20">{item.name}</span>
                      <div className="flex-1">
                        <div className="flex justify-between items-center mb-1">
                          <div className="w-full bg-gray-200 rounded-full h-6 relative">
                            <div 
                              className={`${colors[index]} h-6 rounded-full transition-all duration-500 flex items-center justify-end pr-2`}
                              style={{ width: `${maxValue > 0 ? (item.value / maxValue) * 100 : 0}%` }}
                            >
                              <span className="text-xs text-white font-bold">{formatCurrency(item.value)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Bulk Actions */}
        {selectedDanismanlar.size > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <p className="text-sm text-blue-800">
                <span className="font-semibold">{selectedDanismanlar.size}</span> danışman seçildi
              </p>
              <div className="flex gap-2">
                <button
                  onClick={exportSelectedToExcel}
                  className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm flex items-center gap-1"
                >
                  <FiFileText className="w-4 h-4" />
                  Detaylı Excel
                </button>
                <button
                  onClick={sendBulkEmail}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm flex items-center gap-1"
                >
                  <FiMail className="w-4 h-4" />
                  Toplu Email
                </button>
                <button
                  onClick={() => setSelectedDanismanlar(new Set())}
                  className="px-3 py-1.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
                >
                  İptal
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Report Table */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            {getPeriodText()} - {reportType === 'all' ? 'Tüm Danışmanlar' : 
                                 reportType === 'with-payment' ? 'Alacağı Olanlar' : 'Alacağı Olmayanlar'}
          </h3>
          
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
            </div>
          ) : filteredDanismanlar.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-center py-3 px-2">
                      <input
                        type="checkbox"
                        checked={selectedDanismanlar.size === filteredDanismanlar.length}
                        onChange={selectAllDanismanlar}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                    </th>
                    <th 
                      className="text-left py-3 px-4 font-semibold text-gray-700 cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('id')}
                    >
                      <div className="flex items-center gap-1">
                        ID
                        {sortConfig.key === 'id' && (
                          sortConfig.direction === 'asc' ? <FiChevronUp className="w-4 h-4" /> : <FiChevronDown className="w-4 h-4" />
                        )}
                      </div>
                    </th>
                    <th 
                      className="text-left py-3 px-4 font-semibold text-gray-700 cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('adsoyad')}
                    >
                      <div className="flex items-center gap-1">
                        Danışman Adı
                        {sortConfig.key === 'adsoyad' && (
                          sortConfig.direction === 'asc' ? <FiChevronUp className="w-4 h-4" /> : <FiChevronDown className="w-4 h-4" />
                        )}
                      </div>
                    </th>
                    <th 
                      className="text-center py-3 px-4 font-semibold text-gray-700 cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('dersSayisi')}
                    >
                      <div className="flex items-center justify-center gap-1">
                        Ders Sayısı
                        {sortConfig.key === 'dersSayisi' && (
                          sortConfig.direction === 'asc' ? <FiChevronUp className="w-4 h-4" /> : <FiChevronDown className="w-4 h-4" />
                        )}
                      </div>
                    </th>
                    <th 
                      className="text-right py-3 px-4 font-semibold text-gray-700 cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('toplamAlacak')}
                    >
                      <div className="flex items-center justify-end gap-1">
                        {getPeriodText()} Alacağı
                        {sortConfig.key === 'toplamAlacak' && (
                          sortConfig.direction === 'asc' ? <FiChevronUp className="w-4 h-4" /> : <FiChevronDown className="w-4 h-4" />
                        )}
                      </div>
                    </th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">Durum</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700">İşlemler</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredDanismanlar.map((danisman) => {
                    const periodAmount = getSelectedPeriodAmount(danisman);
                    const periodDetails = getSelectedPeriodDetails(danisman);
                    const isExpanded = expandedRows.has(danisman.id);
                    const isSelected = selectedDanismanlar.has(danisman.id);
                    
                    return (
                      <React.Fragment key={danisman.id}>
                        <tr className={`transition-colors ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                          <td className="text-center py-3 px-2">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleDanismanSelection(danisman.id)}
                              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            />
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600">{danisman.id}</td>
                          <td className="py-3 px-4">
                            <div>
                              <p className="font-medium text-gray-800">{danisman.adsoyad}</p>
                            </div>
                          </td>
                          <td className="text-center py-3 px-4">
                            <span className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-700 rounded-full font-semibold text-sm">
                              {periodDetails.length}
                            </span>
                          </td>
                          <td className="text-right py-3 px-4">
                            <span className={`font-bold ${periodAmount > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                              {formatCurrency(periodAmount)}
                            </span>
                          </td>
                          <td className="text-center py-3 px-4">
                            {periodAmount > 0 ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Alacaklı
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                Alacak Yok
                              </span>
                            )}
                          </td>
                          <td className="text-center py-3 px-4">
                            <div className="flex items-center justify-center gap-1">
                              {periodDetails.length > 0 && (
                                <button
                                  onClick={() => toggleRowExpansion(danisman.id)}
                                  className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors text-sm font-medium"
                                >
                                  {isExpanded ? <FiChevronUp className="w-4 h-4" /> : <FiChevronDown className="w-4 h-4" />}
                                </button>
                              )}
                              <button
                                onClick={() => handleDanismanClick(danisman)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors text-sm font-medium"
                                disabled={periodDetails.length === 0}
                              >
                                <FiEye className="w-4 h-4" />
                                Detay
                              </button>
                            </div>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={7} className="bg-gray-50 px-4 py-3">
                              <div className="text-sm">
                                <h4 className="font-semibold text-gray-700 mb-2">Son 5 Ders</h4>
                                <div className="space-y-1">
                                  {periodDetails.slice(-5).reverse().map((ders, index) => (
                                    <div key={index} className="flex items-center justify-between text-xs">
                                      <span className="text-gray-600">
                                        {formatDate(ders.tarih)} - {ders.konu || 'Konu yok'} ({ders.sure} dk)
                                      </span>
                                      <span className="font-medium text-gray-800">{formatCurrency(ders.ucret)}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <FiUsers className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              <p>Gösterilecek veri bulunamadı</p>
              {searchTerm && (
                <p className="text-sm mt-2">"{searchTerm}" için sonuç bulunamadı</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedDanisman && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-bold text-gray-800">Danışman Payı Detayları</h2>
                  <p className="text-gray-600 mt-1">{selectedDanisman.adsoyad} - {getPeriodText()}</p>
                  <div className="flex gap-4 mt-2 text-sm">
                    <span className="text-gray-600">Toplam Ders: <strong>{getSelectedPeriodDetails(selectedDanisman).length}</strong></span>
                    <span className="text-gray-600">Toplam Alacak: <strong className="text-green-600">{formatCurrency(getSelectedPeriodAmount(selectedDanisman))}</strong></span>
                  </div>
                </div>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {getSelectedPeriodDetails(selectedDanisman).length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Ders ID</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Öğrenci</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Öğretmen</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Konu</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Başlangıç Tarihi</th>
                        <th className="text-center py-3 px-4 font-semibold text-gray-700">Ders Süresi</th>
                        <th className="text-right py-3 px-4 font-semibold text-gray-700">Danışman Payı</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {getSelectedPeriodDetails(selectedDanisman).map((ders) => (
                        <tr key={ders.id} className="hover:bg-gray-50">
                          <td className="py-3 px-4 text-sm text-gray-600">#{ders.id}</td>
                          <td className="py-3 px-4">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{ders.ogrenciAdi || '-'}</div>
                              <div className="text-xs text-gray-500">{ders.ogrenciTelefon || '-'}</div>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{ders.ogretmenAdi || '-'}</div>
                              <div className="text-xs text-gray-500">{ders.ogretmenTelefon || '-'}</div>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-sm">{ders.konu || '-'}</td>
                          <td className="py-3 px-4 text-sm">{formatDate(ders.tarih)}</td>
                          <td className="text-center py-3 px-4">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {ders.sure} dk
                            </span>
                          </td>
                          <td className="text-right py-3 px-4 font-medium text-green-600">
                            {formatCurrency(ders.ucret)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="border-t-2 border-gray-300 bg-gray-50">
                      <tr>
                        <td colSpan="6" className="py-3 px-4 font-bold text-gray-700">
                          Toplam
                        </td>
                        <td className="text-right py-3 px-4 font-bold text-green-700">
                          {formatCurrency(getSelectedPeriodAmount(selectedDanisman))}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  Bu dönemde ders kaydı bulunmamaktadır.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Print Styles */}
      <style jsx>{`
        @media print {
          .no-print {
            display: none !important;
          }
          
          @page {
            margin: 1cm;
          }
          
          table {
            font-size: 12px;
          }
          
          .bg-gradient-to-r {
            background: #f3f4f6 !important;
            color: #1f2937 !important;
          }
        }
      `}</style>
    </div>
  );
};

export default DanismanReportPage;
